"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AuthService = void 0;
const service_1 = require("./service");
const profile_1 = require("../models/profile");
const auth_1 = require("../models/auth");
const authLogin_1 = require("../utils/authLogin");
const register_1 = require("../models/register");
const mapper_1 = require("../utils/mapper");
class AuthService extends service_1.Service {
    constructor() {
        super(...arguments);
        this.userId = 0;
        this.loginKey = '';
        this.sessionData = null;
        /** «Скрытый вход» — не показывать пользователя в списке онлайн (ml/ma +1). */
        this.hiddenLogin = false;
    }
    async handshake() {
        // [version, skin, installer, permission_flag, session_state]
        // Based on decompiled v.java (class e, cmd 26721)
        const res = await this.transport.send('ah', ["1.9.44", "", "com.android.vending", 1, 0]);
        this.checkError(res, 'ah');
        return res;
    }
    async registerDevice(androidIdHash = 12345678, deviceModel = "nextPDA Client", settingsJson = "{}") {
        const res = await this.transport.send('om', [androidIdHash, deviceModel, settingsJson]);
        // Do not throw on om errors since it's non-critical registration
        return res;
    }
    /**
     * Регистрация push-токена на сервере (FCM/HMS).
     * Команда: `ai` (26977) — `[token, bitmask, type]`.
     */
    async registerPush(token, bitmask, type = 0) {
        const res = await this.transport.send('ai', [token, bitmask, type]);
        // Не критично для сессии — как `om`
        return res;
    }
    /**
     * Register new account.
     * Command: "mc" (MEMBER_CREATE) — 25453
     * Wire order (Android AbstractC0136h): login, email, password, captchaCode
     * @param captchaCode 0 on first attempt; digits from image after status 12
     */
    async register(login, email, password, captchaCode = 0) {
        const res = await this.transport.send('mc', [login, email, password, captchaCode]);
        if (res.status === 0) {
            this.userId = res.data[0];
            this.loginKey = res.data[1];
            this.updateTransportUserId();
            try {
                await this.registerDevice();
            }
            catch {
                // Ignore device registration failures during signup
            }
            await this.subscribeEvents(this.userId);
            return {
                success: true,
                status: 0,
                message: (0, register_1.mapRegisterMessage)(0),
                userId: this.userId,
                passHash: this.loginKey,
            };
        }
        if (res.status === 12) {
            const captchaUrl = typeof res.data?.[0] === 'string' ? res.data[0] : '';
            return {
                success: false,
                status: 12,
                captchaUrl,
                message: (0, register_1.mapRegisterMessage)(12),
            };
        }
        const serverText = typeof res.data?.[0] === 'string' ? res.data[0] : undefined;
        const message = (0, register_1.mapRegisterMessage)(res.status ?? -1, serverText);
        return { success: false, status: res.status ?? -1, error: message, message };
    }
    async login(user, pass, captchaHash = 0, captchaText = 0, hiddenLogin = this.hiddenLogin) {
        this.hiddenLogin = hiddenLogin;
        const res = await this.transport.send('ml', (0, authLogin_1.withHiddenLoginFlag)([user, pass, captchaHash, captchaText], hiddenLogin));
        if (res.status === 0) {
            this.userId = res.data[0];
            this.loginKey = res.data[1];
            this.updateTransportUserId();
            try {
                await this.registerDevice();
            }
            catch (e) {
                // Ignore device registration failures during login
            }
            await this.subscribeEvents(this.userId);
            return { success: true, status: 0 };
        }
        else if (res.status === 4) {
            // Captcha required: [requestId, 4, captchaUrl]
            return { success: false, status: 4, captchaUrl: res.data[0], message: (0, auth_1.mapLoginMessage)(4) };
        }
        const message = (0, auth_1.mapLoginMessage)(res.status);
        return { success: false, status: res.status, error: message, message };
    }
    /**
     * Login using existing session (member_id + pass_hash).
     * Command: "ma" (LOGIN) - 24941
     */
    async loginBySession(userId, key, hiddenLogin = this.hiddenLogin) {
        this.userId = userId;
        this.loginKey = key;
        this.hiddenLogin = hiddenLogin;
        this.updateTransportUserId();
        const res = await this.transport.send('ma', (0, authLogin_1.withHiddenLoginFlag)([userId, key], hiddenLogin));
        this.checkError(res, 'ma');
        try {
            await this.registerDevice();
        }
        catch (e) {
            // Ignore device registration failures during loginBySession
        }
        await this.subscribeEvents(userId);
        return res;
    }
    /**
     * Restore session after reconnection.
     * Performs handshake and automatic login if credentials are present.
     */
    async restoreSession() {
        await this.handshake();
        if (this.userId && this.loginKey) {
            return this.loginBySession(this.userId, this.loginKey);
        }
    }
    /**
     * Get current user summary including announcements.
     * Command: "ms" (MEMBER_SUMMARY) - 29549
     * This is the source of announcements like New Year greetings.
     * Response structure:
     *   [0] userId, [1] name, [2] groupId, [3] postsCount, [4] avatar,
     *   [5] postsPerPage, [6] topicsPerPage, [7] premod, [8] readonly,
     *   [9] ban, [10] hasTickets, [11] ticketsCount, [12] ?, [13] ?,
     *   [14] ?, [15] announcements[]
     */
    async getSummary() {
        const res = await this.transport.send('ms');
        this.checkError(res, 'ms');
        const data = res.data;
        // Parse announcements from field index 13 (actual index in raw response)
        // Raw response: [id, status, ...data] so data[0] is userId
        // Note: These announcements often contain warnings strictly for the official Google Play app
        // (e.g., "Downloading APKs is restricted"). Custom clients may choose to ignore them.
        const announcements = [];
        const announcementsRaw = data[13];
        if (announcementsRaw && Array.isArray(announcementsRaw)) {
            for (const item of announcementsRaw) {
                announcements.push({
                    type: item[0],
                    text: item[1],
                    topicId: item[2],
                    messageId: item[3]
                });
            }
        }
        this.sessionData = {
            userId: data[0],
            name: mapper_1.PdaMapper.cleanString(data[1]),
            groupId: data[2],
            groupName: (0, profile_1.getUserGroupName)(data[2]),
            postsCount: data[3],
            avatar: data[4] || '',
            avatarWidth: data[5],
            avatarHeight: data[6],
            premodCount: data[7],
            readonlyUntil: data[8],
            banStatus: data[9],
            hasTickets: data[10] > 0,
            announcements
        };
        return this.sessionData;
    }
    /**
     * @deprecated Use getSummary() instead
     */
    async getProfile() {
        const res = await this.transport.send('ms');
        this.checkError(res, 'ms');
        return res;
    }
    async subscribeEvents(userId) {
        const res = await this.transport.send('ea', [`u${userId}`]);
        this.checkError(res, 'ea');
        return res;
    }
    async fetch(url, options) {
        const headers = new Headers(options?.headers);
        if (this.userId && this.loginKey) {
            headers.append('Cookie', `member_id=${this.userId}; pass_hash=${this.loginKey}`);
        }
        // Ensure User-Agent is consistent
        if (!headers.has('User-Agent')) {
            headers.set('User-Agent', '4PDA/1.9.43 (Android)');
        }
        return globalThis.fetch(url, {
            ...options,
            headers
        });
    }
    /**
     * Get unread counters (QMS, Favorites, Mentions).
     * Command: "mn" (UNREAD_COUNT) - 28269
     *
     * @deprecated Prefer `SyncService.getUnread()` — typed items and full wire params `[version, 0, 0]`.
     */
    async getUnreadCounts(version = 0) {
        const res = await this.transport.send('mn', [version]);
        this.checkError(res, 'mn');
        // Structure: [ReqId, 0, NewVersion, QmsCount, FavCount, MentionsCount, Items]
        return {
            version: res.data[0],
            qms: res.data[1],
            favorites: res.data[2],
            mentions: res.data[3],
            items: res.data[4]
        };
    }
    logout() {
        this.userId = 0;
        this.loginKey = '';
        this.sessionData = null;
        this.hiddenLogin = false;
        this.updateTransportUserId();
    }
    updateTransportUserId() {
        if (this.transport && 'userId' in this.transport) {
            this.transport.userId = this.userId;
        }
    }
}
exports.AuthService = AuthService;
